import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { DataTable } from "components/molecules/table/table";
import { DataTableSkeleton, PageHeader, SectionCard, StatCard, StatusPill } from "components/admin";
import { PaymentCreateForm } from "pages/app/workspace/sections/forms/payments-section-form";
import {
  getPaymentTargets,
  validatePaymentDraft,
} from "pages/app/workspace/sections/helpers/account-section.helpers";
import {
  SectionContentGrid,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatMoney, getApiErrorMessage } from "utils/admin";
import { PaymentsSectionSection } from "pages/app/workspace/sections/styles/payments-section.inline.styles";
export const PaymentsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const [paymentType, setPaymentType] = useState<"merchandise_item_id" | "player_class_id">(
    "merchandise_item_id",
  );
  const [targetId, setTargetId] = useState("");
  const [amount, setAmount] = useState("");
  const { data: response, isLoading } = useQuery({
    queryKey: ["workspace-payments"],
    queryFn: () =>
      workspaceApi.payments.list({
        page: 1,
        per_page: 50,
      }),
  });
  const { data: merchandise = [] } = useQuery({
    queryKey: ["workspace-payment-merchandise"],
    queryFn: () =>
      workspaceApi.merchandise.list({
        sort: "popular",
        per_page: 100,
      }),
  });
  const { data: classes = [] } = useQuery({
    queryKey: ["workspace-player-classes-reference"],
    queryFn: () =>
      workspaceApi.playerClasses.list({
        per_page: 100,
      }),
  });
  const payments = response?.payments || [];
  const targets = getPaymentTargets({
    classes,
    merchandise,
    paymentType,
  });
  const paymentErrors = validatePaymentDraft({
    amount,
    targetId,
  });
  const createMutation = useMutation({
    mutationFn: () =>
      workspaceApi.payments.create({
        [paymentType]: targetId,
        amount: amount ? Number(amount) : undefined,
        otp_required: false,
      }),
    onSuccess: () => {
      toast.success("Payment processed.");
      setTargetId("");
      setAmount("");
      queryClient.invalidateQueries({
        queryKey: ["workspace-payments"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to process this payment."));
    },
  });
  const handlePaymentSubmit = () => {
    const firstError = paymentErrors.target_id || paymentErrors.amount;
    if (firstError) {
      toast.error(firstError);
      return;
    }
    createMutation.mutate();
  };
  return (
    <>
      <PageHeader
        title="Payments"
        description="Review processed payments and create new payment records against classes or merchandise."
      />

      <SectionStatsGrid>
        <StatCard label="Payments" value={payments.length} />
        <StatCard
          label="Completed"
          value={payments.filter((payment: any) => payment.status === "completed").length}
        />
        <StatCard
          label="Pending"
          value={payments.filter((payment: any) => payment.status === "pending").length}
        />
        <StatCard
          label="Volume"
          value={formatMoney(
            payments.reduce((sum: number, payment: any) => sum + Number(payment.amount || 0), 0),
          )}
        />
      </SectionStatsGrid>

      <PaymentsSectionSection>
        <SectionContentGrid>
          <SectionCard subtitle="Use the payments endpoint directly" title="Create Payment">
            <PaymentCreateForm
              amount={amount}
              amountError={paymentErrors.amount}
              isSubmitting={createMutation.isPending}
              onAmountChange={setAmount}
              onPaymentTypeChange={(value) => {
                setPaymentType(value);
                setTargetId("");
              }}
              onSubmit={handlePaymentSubmit}
              onTargetChange={setTargetId}
              paymentType={paymentType}
              targetError={paymentErrors.target_id}
              targetId={targetId}
              targets={targets}
            />
          </SectionCard>

          <SectionCard subtitle="Recent payment activity returned by the API" title="Payment Feed">
            <DataTable
              canEdit={false}
              canRemove={false}
              columns={[
                {
                  label: "Target",
                  accessor: (row: any) => row.payment_target_name || "Unknown",
                },
                {
                  label: "Type",
                  accessor: (row: any) => row.payment_target_type || "Unknown",
                },
                {
                  label: "Amount",
                  accessor: (row: any) => row.formatted_amount || formatMoney(row.amount),
                },
                {
                  label: "Status",
                  accessor: (row: any) => <StatusPill value={row.status} />,
                },
              ]}
              data={payments}
              emptyDescription="Payments created through classes or storefront actions will appear here."
              emptyTitle="No payments yet"
              hideNav
              isLoading={isLoading}
              loadingState={<DataTableSkeleton columns={4} rows={5} />}
            />
          </SectionCard>
        </SectionContentGrid>
      </PaymentsSectionSection>
    </>
  );
};
